// src/index.js
import 'react-app-polyfill/ie9';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { createStore, applyMiddleware } from 'redux';
import { createLogger } from 'redux-logger';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import reducers from './reducers';
import './index.css';
import App from './components/App';
import * as serviceWorker from './serviceWorker';
import { IntlProvider } from "react-intl";
import { addLocaleData } from "react-intl";
import enLocaleData from 'react-intl/locale-data/en';
import frLocaleData from 'react-intl/locale-data/fr';
import messages_en from "./utils/messages/en.json";
import messages_fr from "./utils/messages/fr.json";
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core';
import keycloak, { initSso, getDisplayName } from "./auth/keycloak";
import { setSession, setSsoReady, resetAuth } from "./actions/authentication";
import { initialState as authenticationInitialState } from "./reducers/authentication";
import { readRestorableSession, clearStoredSession, purgeLegacyKeys, MODE_KEYCLOAK } from "./auth/session";

addLocaleData(enLocaleData);
addLocaleData(frLocaleData);

const messages = {
    'fr': messages_fr,
    'en': messages_en
};

// Legacy `loggedIn` flags are dropped before anything reads the state (no ghost sign-in after
// a deployment); the signed-in state is restored from its single mirror only, and a Keycloak session is
// never restored from it (only a successful silent check-sso below signs a Keycloak user in).
purgeLegacyKeys();

export const store = createStore(
    reducers(),
    { authentication: { ...authenticationInitialState, session: readRestorableSession() } },
    composeWithDevTools(
        applyMiddleware(
            thunk,
            createLogger({ collapsed: true }),
        ),
    ),
);
// "Login with Keycloak" mode: silently check for an existing Keycloak session
// (e.g. right after the redirect back from the login page) and record the signed-in state so the
// app routes to /admin. A stored Keycloak state whose SSO session is gone is dropped (the check is
// bounded: a silent check that never answers counts as signed out).
// No secret involved (PKCE public client).
initSso().then((authenticated) => {
    if (authenticated && keycloak.authenticated) {
        store.dispatch(setSession({ mode: MODE_KEYCLOAK, displayName: getDisplayName() }));
    } else {
        const current = store.getState().authentication.session;
        if (current && current.mode === MODE_KEYCLOAK) {
            clearStoredSession();
            store.dispatch(resetAuth());
        }
    }
    store.dispatch(setSsoReady());
});

const history = createBrowserHistory();
const theme = createMuiTheme({
    typography: {
        useNextVariants: true,
    },
    overrides: {

        MuiButton: {
            label: {
                color: "#fff"
            }
        }
    },
});
const ROOT = (
    <IntlProvider locale={'en'} messages={messages['en']}>
        <Provider store={store}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                <Router history={history}>
                    <App />
                </Router>
            </MuiThemeProvider>
        </Provider>
    </IntlProvider>
);
ReactDOM.render(ROOT, document.getElementById('root'));
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
